import React, { useEffect, useState } from "react";
import { Button, message, Tabs, Tag, Spin } from "antd";
import { CopyOutlined, SwapOutlined, HistoryOutlined, LoadingOutlined, ReloadOutlined, DisconnectOutlined } from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/global-background.css";
import "../styles/BridgePage.css";
import { useAccount, usePublicClient, useDisconnect, useConnect } from "wagmi";
import { api } from "@/api/api";
import { useBridge } from "../hooks/useBridge";
import { useNctBalance } from "../hooks/useNctBalance";
import { CONTRACTS } from "../contracts/config";

// 定义类型接口
interface TokenWithAddress {
  name: string;
  symbol: string;
  address: string;
  logo: string;
}

interface TokenWithoutAddress {
  name: string;
  symbol: string;
  logo: string;
}

type Token = TokenWithAddress | TokenWithoutAddress;

// 交易历史类型
interface Transaction {
  id: string;
  type: "swap" | "withdraw";
  fromToken: string;
  toToken: string;
  amount: string;
  timestamp: number;
  status: "completed" | "pending" | "failed";
  txHash?: string;
}

const MEM_TOKEN: TokenWithAddress = {
  name: "Nova Crafter Lab",
  symbol: "NClab",
  address: CONTRACTS.NCT_TOKEN,
  logo: "/images/nclab.png",
};

const NOVA_TOKEN: TokenWithoutAddress = {
  name: "NOVA",
  symbol: "NOVA",
  logo: "", // 可补充logo
};

const BridgePage: React.FC = () => {
  const [direction, setDirection] = useState(true);
  const [fromAmount, setFromAmount] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  // balance
  const [novaBalance, setNovaBalance] = useState<string>("--");

  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { disconnect } = useDisconnect();
  const { connect, connectors } = useConnect();
  
  // 使用独立的NCT余额hook
  const { balance: nclabBalance, isApproving } = useNctBalance();
  
  const { 
    state: bridgeState, 
    nctBalance: bridgeNctBalance, 
    nctAllowance,
    novaBalance: bridgeNovaBalance,
    minWithdrawAmount,
    withdrawFeeRate,
    transactions,
    depositNclabToNova,
    withdrawNovaToNclab,
    checkAllowance, 
    requestApproval,
    calculateWithdrawFee,
    getNetWithdrawAmount,
    validateWithdrawAmount,
    reset: resetBridge,
    refetchBalance: refetchBridgeBalance,
    refetchNovaBalance: refetchBridgeNovaBalance,
    refetchTransactions,
    retryFailedTransactions,
  } = useBridge();
  
  const fromToken = direction ? MEM_TOKEN : NOVA_TOKEN;
  const toToken = direction ? NOVA_TOKEN : MEM_TOKEN;

  // 同步NOVA余额
  useEffect(() => {
    setNovaBalance(bridgeNovaBalance || "0");
  }, [bridgeNovaBalance]);

  // 强制重置所有状态
  const handleForceReset = () => {
    resetBridge();
    setFromAmount("");
    message.success('状态已重置');
  };

  // 类型守卫函数
  const hasAddress = (token: Token): token is TokenWithAddress => {
    return "address" in token;
  };

  // 复制合约地址
  const handleCopy = (address: string) => {
    navigator.clipboard.writeText(address);
    message.success("合约地址已复制");
  };

  // 跳转到PancakeSwap兑换
  const handlePancakeSwap = (address: string): void => {
    const pancakeUrl = `https://pancakeswap.finance/swap?outputCurrency=${address}`;
    window.open(pancakeUrl, '_blank');
  };

  // 切换方向（带动画）
  const handleSwitch = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    setDirection(!direction);
    setFromAmount("");

    // 动画结束后重置状态
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  // 输入框变化，自动同步另一边
  const handleFromAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 只允许数字和小数点
    if (/^\d*\.?\d*$/.test(value)) {
      setFromAmount(value);
    }
  };

  // 右侧提示
  const getToSideLabel = () => {
    if (!fromAmount) return "Enter an amount";
    return direction ? "置换" : "提款";
  };

  // 格式化时间
  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}天前`;
    if (hours > 0) return `${hours}小时前`;
    if (minutes > 0) return `${minutes}分钟前`;
    return "刚刚";
  };

  // 获取状态配置
  const getStatusConfig = (status: string) => {
    const statusMap = {
      completed: { color: "#4caf50", bgColor: "rgba(76, 175, 80, 0.1)", text: "已完成" },
      pending: { color: "#ff9800", bgColor: "rgba(255, 152, 0, 0.1)", text: "进行中" },
      failed: { color: "#f44336", bgColor: "rgba(244, 67, 54, 0.1)", text: "失败" },
    };
    return statusMap[status as keyof typeof statusMap] || statusMap.completed;
  };

  // 验证金额是否有效
  const isValidAmount = () => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      return false;
    }
    
    if (direction) {
      // NClab -> NOVA，移除最小金额限制
      const amount = parseFloat(fromAmount);
      
      // 只检查余额限制
      const currentBalance = parseFloat(nclabBalance || '0');
      return amount <= currentBalance;
    } else {
      // NOVA -> NClab，使用新的验证函数
      const validation = validateWithdrawAmount(fromAmount);
      return validation.isValid;
    }
  };

  // 获取金额验证错误信息
  const getAmountError = () => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      return null;
    }
    
    if (direction) {
      // NClab -> NOVA，移除最小金额限制
      const amount = parseFloat(fromAmount);
      const currentBalance = parseFloat(nclabBalance || '0');
      
      if (amount > currentBalance) {
        return `存款金额不能超过当前余额 ${currentBalance.toLocaleString()} NClab`;
      }
    } else {
      // NOVA -> NClab，使用新的验证函数
      const validation = validateWithdrawAmount(fromAmount);
      if (!validation.isValid) {
        return validation.error;
      }
    }
    
    return null;
  };

  // 获取按钮文本
  const getButtonText = () => {
    if (bridgeState.isLoading) {
      if (bridgeState.isDepositing) {
        return '存款中...';
      } else if (isApproving) {
        return '授权中...';
      } else {
        return '处理中...';
      }
    }
    
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      return 'Enter an amount';
    }
    
    const amountError = getAmountError();
    if (amountError) {
      return amountError;
    }
    
    if (direction) {
      // NClab -> NOVA
      if (!checkAllowance(fromAmount)) {
        return `授权 ${fromAmount} ${fromToken.symbol}`;
      } else {
        return `确认存款 ${fromAmount} ${fromToken.symbol}`;
      }
    } else {
      // NOVA -> NClab (提款)
      return `确认提款 ${fromAmount} ${fromToken.symbol}`;
    }
  };

  // 检查按钮是否应该禁用
  const isButtonDisabled = () => {
    return !isValidAmount() || isAnimating || bridgeState.isLoading;
  };

  // 修改handleBridge函数
  const handleBridge = async () => {
    if (!address) {
      message.error('请先连接钱包');
      return;
    }

    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      message.error('请输入有效的数量');
      return;
    }

    try {
      if (direction) {
        // NClab -> NOVA 存款
        const result = await depositNclabToNova(fromAmount);
        if (result) {
          // 存款成功，清空输入框
          setFromAmount("");
        }
        // 如果返回false，可能是需要授权或其他原因，不清空输入框
      } else {
        // NOVA -> NClab 提款
        await withdrawNovaToNclab(fromAmount);
      }
      
    } catch (error) {
      // 静默处理错误
    }
  };

  // 设置最大可用金额
  const setMaxAmount = () => {
    if (direction) {
      // NClab -> NOVA，移除最小金额限制
      const currentBalance = parseFloat(nclabBalance || '0');
      
      if (currentBalance > 0) {
        setFromAmount(currentBalance.toString());
      } else {
        message.warning(`余额不足`);
      }
    } else {
      // NOVA -> NClab
      const currentBalance = parseFloat(novaBalance || '0');
      const minAmount = parseFloat(minWithdrawAmount);
      
      if (currentBalance >= minAmount) {
        setFromAmount(currentBalance.toString());
      } else {
        message.warning(`余额不足，最小提款金额为 ${minAmount.toLocaleString()} NOVA`);
      }
    }
  };

  // 钱包重连处理
  const handleReconnectWallet = () => {
    try {
      disconnect();
      setTimeout(() => {
        const injectedConnector = connectors.find(connector => connector.id === 'injected');
        if (injectedConnector) {
          connect({ connector: injectedConnector });
        }
      }, 100);
    } catch (error) {
      message.error('重连失败，请手动连接钱包');
    }
  };

  // 检查是否为钱包连接相关错误
  const isWalletConnectionError = (error: string): boolean => {
    return error.includes('用户地址无效') || 
           error.includes('请重新连接钱包') || 
           error.includes('钱包未连接');
  };

  // 渲染桥接面板
  const renderBridgePanel = () => (
    <div className="bridge-container">
      {/* From Token */}
      <div className={`token-section ${isAnimating ? "animating" : ""}`}>
        <div className="token-section-header">
          <span className="token-label">从</span>
          <span className="token-balance">
            余额: {direction ? nclabBalance : novaBalance}{" "}
            {direction ? MEM_TOKEN.symbol : NOVA_TOKEN.symbol}
          </span>
        </div>
        <div className="token-input-container">
          <div className="token-info">
            {fromToken.logo ? (
              <img
                src={fromToken.logo}
                alt={fromToken.symbol}
                className="token-logo"
              />
            ) : (
              <div className="token-logo-placeholder">
                {fromToken.symbol[0]}
              </div>
            )}
            <div className="token-details">
              <div className="token-name">{fromToken.name}</div>
              <div className="token-symbol">{fromToken.symbol}</div>
              {hasAddress(fromToken) && (
                <div className="token-address">
                  <span>
                    {fromToken.address.slice(0, 10)}...
                    {fromToken.address.slice(-4)}
                  </span>
                  <CopyOutlined
                    onClick={() => handleCopy(fromToken.address)}
                    className="copy-icon"
                    title="复制地址"
                  />
                  {fromToken.symbol === "NClab" && (
                    <span 
                      className="pancake-link"
                      onClick={() => handlePancakeSwap(fromToken.address)}
                      title="没有nclab？从pancakeswap获取"
                    >
                      🥞
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="token-amount-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="number"
              className="token-amount-input"
              placeholder="0.0"
              value={fromAmount}
              onChange={handleFromAmountChange}
              min="0"
              step="any"
              disabled={bridgeState.isLoading}
              style={{ paddingRight: '60px' }}
            />
            <div className="amount-input-actions" style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)'
            }}>
              <Button 
                size="small" 
                type="link" 
                onClick={setMaxAmount}
                disabled={bridgeState.isLoading}
                style={{ 
                  fontSize: '12px', 
                  padding: '2px 8px',
                  height: '24px',
                  color: '#1890ff'
                }}
              >
                最大值
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Switch Button */}
      <div className="switch-container">
        <Button
          shape="circle"
          className={`switch-button ${isAnimating ? "animating" : ""}`}
          onClick={handleSwitch}
          icon={<SwapOutlined />}
          size="large"
          disabled={isAnimating || bridgeState.isLoading}
        />
      </div>

      {/* To Token */}
      <div className={`token-section ${isAnimating ? "animating" : ""}`}>
        <div className="token-section-header">
          <span className="token-label">到</span>
          <span className="token-balance">
            余额: {direction ? novaBalance : nclabBalance}{" "}
            {direction ? NOVA_TOKEN.symbol : MEM_TOKEN.symbol}
          </span>
        </div>
        <div className="token-input-container">
          <div className="token-info">
            {toToken.logo ? (
              <img
                src={toToken.logo}
                alt={toToken.symbol}
                className="token-logo"
              />
            ) : (
              <div className="token-logo-placeholder">{toToken.symbol[0]}</div>
            )}
            <div className="token-details">
              <div className="token-name">{toToken.name}</div>
              <div className="token-symbol">{toToken.symbol}</div>
              {hasAddress(toToken) && (
                <div className="token-address">
                  <span>
                    {toToken.address.slice(0, 10)}...
                    {toToken.address.slice(-4)}
                  </span>
                  <CopyOutlined
                    onClick={() => handleCopy(toToken.address)}
                    className="copy-icon"
                    title="复制地址"
                  />
                  {toToken.symbol === "NClab" && (
                    <span 
                      className="pancake-link"
                      onClick={() => handlePancakeSwap(toToken.address)}
                      title="没有nclab？从pancakeswap获取"
                    >
                      🥞
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="token-amount-output">{fromAmount || "0.0"}</div>
        </div>
      </div>

      {/* Exchange Rate */}
      <div className="exchange-info">
        <div className="exchange-rate">
          <span>
            汇率: 1 {fromToken.symbol} = 1 {toToken.symbol}
          </span>
        </div>
        
        {/* 显示合约参数信息 */}
        {!direction && (
          <div className="withdraw-info" style={{ 
            marginTop: '12px',
            padding: '20px',
            background: 'rgba(157, 0, 255, 0.05)',
            border: '1px solid rgba(157, 0, 255, 0.15)',
            borderRadius: '16px',
            fontSize: '14px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontWeight: 600, 
              marginBottom: '16px',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              提款参数
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>最低提款金额:</span>
              <span style={{ fontWeight: 500, color: '#fff' }}>
                {parseFloat(minWithdrawAmount).toLocaleString()} NOVA
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: fromAmount && parseFloat(fromAmount) > 0 ? '16px' : '0' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>提款手续费率:</span>
              <span style={{ fontWeight: 500, color: '#fff' }}>
                {(parseFloat(withdrawFeeRate) * 100).toFixed(2)}%
              </span>
            </div>
            
            {fromAmount && parseFloat(fromAmount) > 0 && (
              <div style={{ 
                borderTop: '1px solid rgba(157, 0, 255, 0.15)', 
                paddingTop: '16px'
              }}>
                <div style={{ 
                  color: 'rgba(255, 255, 255, 0.8)', 
                  fontWeight: 600, 
                  marginBottom: '12px',
                  fontSize: '0.875rem'
                }}>
                  费用明细
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>提款金额:</span>
                  <span style={{ fontWeight: 500, color: '#fff' }}>
                    {parseFloat(fromAmount).toLocaleString()} NOVA
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>手续费:</span>
                  <span style={{ fontWeight: 500, color: '#ff7a00' }}>
                    -{parseFloat(calculateWithdrawFee(fromAmount)).toLocaleString()} NOVA
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(157, 0, 255, 0.1)'
                }}>
                  <span style={{ color: 'rgba(255, 183, 0, 0.8)', fontWeight: 600 }}>实际到账:</span>
                  <span style={{ fontWeight: 600, color: 'rgba(255, 183, 0, 0.9)', fontSize: '1.1em' }}>
                    {parseFloat(getNetWithdrawAmount(fromAmount)).toLocaleString()} NClab
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Button */}
      <Button
        type="primary"
        className="bridge-action-button"
        size="large"
        onClick={handleBridge}
        disabled={isButtonDisabled()}
        loading={bridgeState.isLoading}
        danger={!!getAmountError()}
      >
        {getButtonText()}
      </Button>

      {/* 重置按钮 - 当卡在loading状态时显示 */}
      {bridgeState.isLoading && (
        <Button
          type="default"
          size="small"
          onClick={handleForceReset}
          style={{ marginTop: '10px' }}
          danger
        >
          强制重置状态
        </Button>
      )}

      {/* 清理本地记录按钮 - 当有待处理交易时显示 */}
      {bridgeState.pendingTransactions.length > 0 && (
        <Button
          type="default"
          size="small"
          onClick={() => {
            // 通过window对象访问清理函数
            if (typeof window !== 'undefined') {
              const key = `pending_transactions_${address}`;
              localStorage.removeItem(key);
              window.location.reload();
            }
          }}
          style={{ marginTop: '10px', marginLeft: '10px' }}
          danger
        >
          清理本地记录
        </Button>
      )}

      {/* 错误信息显示 */}
      {bridgeState.error && (
        <div className="error-message" style={{ 
          color: '#ff4d4f', 
          textAlign: 'center', 
          marginTop: '10px',
          padding: '15px',
          background: 'rgba(255, 77, 79, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 77, 79, 0.2)'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 500 }}>
            {bridgeState.error}c
          </div>
          
          {/* 如果是钱包连接相关错误，显示重连按钮 */}
          {isWalletConnectionError(bridgeState.error) && (
            <div style={{ marginTop: '12px' }}>
              <Button
                type="primary"
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleReconnectWallet}
                style={{ marginRight: '8px' }}
              >
                重新连接钱包
              </Button>
              <Button
                size="small"
                icon={<DisconnectOutlined />}
                onClick={() => {
                  resetBridge();
                  setFromAmount("");
                }}
              >
                清除错误
              </Button>
            </div>
          )}
          
          {/* 对于其他错误，提供通用的重置选项 */}
          {!isWalletConnectionError(bridgeState.error) && (
            <div style={{ marginTop: '12px' }}>
              <Button
                size="small"
                onClick={() => {
                  resetBridge();
                  setFromAmount("");
                }}
              >
                清除错误并重试
              </Button>
            </div>
          )}
        </div>
      )}

      {/* 钱包连接状态检查 */}
      {!isConnected && (
        <div className="wallet-connection-notice" style={{
          marginTop: '15px',
          padding: '15px',
          background: 'rgba(255, 193, 7, 0.1)',
          border: '1px solid rgba(255, 193, 7, 0.3)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px', color: '#faad14', fontWeight: 500 }}>
            ⚠️ 钱包未连接
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            请连接钱包以使用桥接功能
          </div>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              const injectedConnector = connectors.find(connector => connector.id === 'injected');
              if (injectedConnector) {
                connect({ connector: injectedConnector });
              }
            }}
          >
            连接钱包
          </Button>
        </div>
      )}

      {/* 地址验证失败提示 */}
      {isConnected && !address && (
        <div className="address-validation-notice" style={{
          marginTop: '15px',
          padding: '15px',
          background: 'rgba(255, 77, 79, 0.1)',
          border: '1px solid rgba(255, 77, 79, 0.2)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px', color: '#f5222d', fontWeight: 500 }}>
            ❌ 地址获取失败
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            无法获取钱包地址，请重新连接钱包
          </div>
          <Button
            size="small"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleReconnectWallet}
          >
            重新连接钱包
          </Button>
        </div>
      )}

      {/* PancakeSwap 提示卡片 */}
      <div className="pancakeswap-notice-card">
        <div className="notice-icon">🥞</div>
        <div className="notice-content">
          <h4 className="notice-title">没有 NClab ？</h4>
          <button 
            className="notice-button"
            onClick={() => handlePancakeSwap(MEM_TOKEN.address)}
          >
            前往 PancakeSwap 兑换
          </button>
        </div>
      </div>

      {/* 金额验证错误提示 */}
      {fromAmount && getAmountError() && (
        <div className="amount-error-message" style={{ 
          color: '#ff4d4f', 
          textAlign: 'center', 
          marginTop: '8px',
          fontSize: '14px'
        }}>
          {getAmountError()}
        </div>
      )}
    </div>
  );

  // 渲染自定义交易历史
  const renderTransactionHistory = (): React.ReactElement => (
    <div className="transaction-history">
      <div className="history-header">
        <HistoryOutlined />
        <span>交易历史</span>
        {bridgeState.pendingTransactions.length > 0 && (
          <Button 
            size="small" 
            type="primary" 
            onClick={retryFailedTransactions}
            loading={bridgeState.isTransactionInProgress}
            style={{ marginLeft: 'auto' }}
          >
            重试失败交易 ({bridgeState.pendingTransactions.filter(tx => tx.status === 'failed').length})
          </Button>
        )}
      </div>
      
      {bridgeState.isLoadingTransactions ? (
        <div className="transaction-loading">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          <div>加载交易历史中...</div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="transaction-empty">
          <div className="empty-icon">📝</div>
          <div className="empty-text">暂无交易记录</div>
        </div>
      ) : (
        <div className="transaction-list">
          {transactions.map((transaction) => {
            const statusConfig = getStatusConfig(transaction.status);
            return (
              <div key={transaction.id} className="transaction-card">
                <div className="transaction-card-header">
                  <div className="transaction-info">
                    <div className="transaction-type">
                      <span 
                        className="type-badge"
                        style={{ 
                          backgroundColor: transaction.type === "deposit" ? "rgba(33, 150, 243, 0.1)" : "rgba(76, 175, 80, 0.1)",
                          color: transaction.type === "deposit" ? "#2196f3" : "#4caf50"
                        }}
                      >
                        {transaction.type === "deposit" ? "存款" : transaction.type === "withdraw" ? "提款" : "转账"}
                      </span>
                      <span className="transaction-pair">
                        NClab → NOVA
                      </span>
                    </div>
                    <div className="transaction-status">
                      <span 
                        className="status-badge"
                        style={{ 
                          backgroundColor: statusConfig.bgColor,
                          color: statusConfig.color
                        }}
                      >
                        {statusConfig.text}
                      </span>
                    </div>
                  </div>
                  <div className="transaction-time">
                    {formatTime(new Date(transaction.timestamp).getTime())}
                  </div>
                </div>
                
                <div className="transaction-card-body">
                  <div className="transaction-amount">
                    <span className="amount-label">数量</span>
                    <span className="amount-value">
                      {transaction.amount} NClab
                    </span>
                  </div>
                  
                  {transaction.txHash && (
                    <div className="transaction-hash">
                      <span className="hash-label">交易哈希</span>
                      <span className="hash-value">
                        {transaction.txHash.slice(0, 10)}...{transaction.txHash.slice(-8)}
                        <CopyOutlined
                          onClick={() => handleCopy(transaction.txHash)}
                          className="copy-icon"
                          title="复制交易哈希"
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 待处理交易提示 */}
      {bridgeState.pendingTransactions.length > 0 && (
        <div className="pending-transactions-notice" style={{
          marginTop: '20px',
          padding: '15px',
          background: 'rgba(255, 193, 7, 0.1)',
          border: '1px solid rgba(255, 193, 7, 0.3)',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <LoadingOutlined style={{ color: '#ffc107', marginRight: '8px' }} />
            <span style={{ fontWeight: 500 }}>待处理交易</span>
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            有 {bridgeState.pendingTransactions.length} 个交易需要手动重试处理。
          </div>
          {bridgeState.pendingTransactions.some(tx => tx.status === 'failed') && (
            <div style={{ fontSize: '14px', color: '#f5222d', marginTop: '5px' }}>
              其中 {bridgeState.pendingTransactions.filter(tx => tx.status === 'failed').length} 个交易失败，需要手动重试。
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="nova-container">
      <div className="nova-grid"></div>
      
      <Navbar language="zh" />

      <div className="content">
        <div className="title-section">
          <h1 className="nova-title">代币桥接</h1>
          <p className="hero-subtitle">NClab ⇄ NOVA</p>
        </div>

        <div className="bridge-main-content">
          <Tabs
            defaultActiveKey="bridge"
            className="bridge-tabs"
            items={[
              {
                key: "bridge",
                label: "桥接",
                children: renderBridgePanel(),
              },
              {
                key: "history",
                label: "历史记录",
                children: renderTransactionHistory(),
              },
            ]}
          />
        </div>
      </div>

      <Footer language="zh" setLanguage={() => {}} />
    </div>
  );
};

export default BridgePage;
